import { getMonth, getISODay, isValid, parse, parseISO } from 'date-fns';
import format from 'date-fns/format';
import { ru } from 'date-fns/locale';

const DISPLAY_DATE_FORMATS = [
  { pattern: /^\d{2}\.\d{2}\.\d{4}$/, input: 'dd.MM.yyyy', output: 'yyyy-MM-dd' },
  { pattern: /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/, input: 'dd.MM.yyyy HH:mm', output: 'yyyy-MM-dd HH:mm' },
  { pattern: /^\d{2}\.\d{2}\.\d{4} \(\d{2}:\d{2}\)$/, input: 'dd.MM.yyyy (HH:mm)', output: 'yyyy-MM-dd HH:mm' },
];
const UTC_ISO_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}):\d{2}(?:\.\d+)?Z$/;

export const MONTH_NAME_RU_MAP = {
  0: 'Январь',
  1: 'Февраль',
  2: 'Март',
  3: 'Апрель',
  4: 'Май',
  5: 'Июнь',
  6: 'Июль',
  7: 'Август',
  8: 'Сентябрь',
  9: 'Октябрь',
  10: 'Ноябрь',
  11: 'Декабрь',
};

export const MONTH_NAME_RU_GENITIVE_MAP = {
  0: 'Января',
  1: 'Февраля',
  2: 'Марта',
  3: 'Апреля',
  4: 'Мая',
  5: 'Июня',
  6: 'Июля',
  7: 'Августа',
  8: 'Сентября',
  9: 'Октября',
  10: 'Ноября',
  11: 'Декабря',
};

export const DAY_OF_WEEK_NAME_SHORT_RU_MAP = {
  1: 'Пн',
  2: 'Вт',
  3: 'Ср',
  4: 'Чт',
  5: 'Пт',
  6: 'Сб',
  7: 'Вс',
};

export const dateFnsFormat = {
  parseDate(value) {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    if (typeof value !== 'string') return null;

    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const isoDate = parseISO(value);
      return isValid(isoDate) ? isoDate : null;
    }

    const normalized = this.normalizeDate(value);
    if (!normalized) return null;
    const date = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(normalized)
      ? parse(normalized, 'yyyy-MM-dd HH:mm', new Date())
      : parseISO(normalized);
    return isValid(date) ? date : null;
  },
  normalizeDate(value) {
    if (!value) return null;
    if (typeof value !== 'string') return value;

    const utcTimestamp = value.match(UTC_ISO_TIMESTAMP);
    if (utcTimestamp) {
      return isValid(parseISO(value)) ? `${utcTimestamp[1]} ${utcTimestamp[2]}` : null;
    }

    const dateFormat = DISPLAY_DATE_FORMATS.find(({ pattern }) => pattern.test(value));
    if (!dateFormat) return value;

    const date = parse(value, dateFormat.input, new Date());
    return isValid(date) ? format(date, dateFormat.output) : null;
  },
  dateToAge(date) {
    return new Date().getFullYear() - new Date(date).getFullYear() || 'Не корректен';
  },
  getDay(date) {
    if (!date) {
      return null;
    }

    return format(new Date(date), 'dd');
  },
  readableDays(date) {
    return format(new Date(date), 'dd');
  },
  readableDate(date) {
    if (!date) {
      return null;
    }

    return format(new Date(date), 'dd.MM.yyyy');
  },
  readableDateWithShortYear(date) {
    return format(new Date(date), 'dd.MM.yy');
  },
  readableDateWithoutYear(date) {
    return format(new Date(date), 'dd.MM');
  },
  readableDateWithTime(date) {
    if (!date) {
      return null;
    }

    return format(new Date(date), 'dd.MM.yyyy (HH:mm)', { locale: ru });
  },
  readableTime(date) {
    if (!date) {
      return null;
    }

    return format(new Date(date), 'HH:mm');
  },
  readableDateTime(date) {
    if (!date) {
      return null;
    }

    return format(new Date(date), 'dd.MM.yyyy HH:mm');
  },
  readableDateWithMonth(date) {
    if (!date) {
      return false;
    }

    return format(new Date(date), 'dd MMMM yyyy HH:mm', { locale: ru });
  },
  readableShortDateWithMonth(date) {
    if (!date) {
      return false;
    }

    return format(new Date(date), 'dd MMMM yyyy', { locale: ru });
  },
  dateShort(date) {
    if (!date) {
      return false;
    }

    return format(new Date(date), 'dd.MM.yyyy');
  },
  readableMonthName(date) {
    return MONTH_NAME_RU_MAP[getMonth(new Date(date))];
  },
  readableMonthGenitiveName(date) {
    return MONTH_NAME_RU_GENITIVE_MAP[getMonth(new Date(date))];
  },
  readableWeekNameShort(date) {
    return DAY_OF_WEEK_NAME_SHORT_RU_MAP[getISODay(new Date(date))];
  },
};
